import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import attendanceService from '../services/attendanceService';
import classService from '../services/classService';
import type { AttendanceRecord } from '../types';

/**
 * High-Performance Daily Attendance Register Page
 */
export const Attendance: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const [searchParams] = useSearchParams();

  const numericClassId = parseInt(classId || '0', 10);
  const targetDate = searchParams.get('date') || new Date().toISOString().split('T')[0];

  const [className, setClassName] = useState<string>('');
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const fetchRegister = async () => {
      try {
        const [data, classesRes] = await Promise.all([
          attendanceService.getDailyRegister(numericClassId, targetDate),
          classService.getMyClasses()
        ]);
        setRecords(data);
        const activeClass = classesRes.find(c => c.id === numericClassId);
        if (activeClass) {
          setClassName(activeClass.class_name);
        } else {
          setClassName(`Class #${numericClassId}`);
        }
      } catch (err: any) {
        setError('Failed to fetch class attendance register from database.');
      } finally {
        setLoading(false);
      }
    };

    fetchRegister();
  }, [numericClassId, targetDate]);

  /**
   * Compiles the updated states and pushes back to the server in the background
   */
  const triggerAutoSave = async (updatedRecords: AttendanceRecord[]) => {
    setError(null);
    setSaving(true);
    try {
      const payload = updatedRecords.map((r) => ({
        pupil_id: r.pupil_id,
        status: r.status,
        remarks: r.remarks || ''
      }));

      const success = await attendanceService.bulkSaveRegister(
        numericClassId,
        targetDate,
        payload
      );

      if (success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (err: any) {
      setError('Background Sync Error: Failed to save changes to database.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Modifies a single pupil's attendance status and triggers auto-save
   */
  const handleStatusChange = (pupilId: number, status: 'P' | 'A' | 'L' | 'E') => {
    const updated = records.map((r) => {
      if (r.pupil_id === pupilId) {
        return { ...r, status };
      }
      return r;
    });
    setRecords(updated);
    triggerAutoSave(updated);
  };

  /**
   * Modifies the custom remarks field for a pupil
   */
  const handleRemarksChange = (pupilId: number, remarks: string) => {
    setRecords((prev) =>
      prev.map((r) => {
        if (r.pupil_id === pupilId) {
          return { ...r, remarks };
        }
        return r;
      })
    );
  };

  /**
   * A massive timesaver for teachers: marks all students present and triggers save
   */
  const handleMarkAllPresent = () => {
    const updated = records.map((r) => ({
      ...r,
      status: 'P' as const
    }));
    setRecords(updated);
    triggerAutoSave(updated);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', height: '50vh', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '16px', color: 'var(--text-muted)' }}>Loading attendance register...</p>
        </div>
      </div>
    );
  }

  // Label configuration for nice visual badge overlays
  const statusBadges = {
    P: { label: 'Present', color: 'var(--color-success)', bg: 'var(--color-success-light)' },
    A: { label: 'Absent', color: 'var(--color-danger)', bg: 'var(--color-danger-light)' },
    L: { label: 'Late', color: 'var(--color-warning)', bg: 'var(--color-warning-light)' },
    E: { label: 'Excused', color: 'var(--text-muted)', bg: 'var(--bg-surface-hover)' }
  };

  return (
    <div style={{ animation: 'var(--transition-normal) fadeIn', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Page header and quick sync info */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '1px solid var(--border-color)', paddingBottom: '20px' }}>
        <div>
          <Link to={`/classes/${numericClassId}`} style={{ textDecoration: 'none', color: 'var(--text-muted)', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            &larr; Return to Dashboard
          </Link>
          <h1 style={{ fontSize: '26px', fontWeight: 800 }}>📅 Daily Attendance Register</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>
            Class: <strong>{className}</strong> | School Day: <strong>{targetDate}</strong>
          </p>
        </div>

        {/* Sync Status Banner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={handleMarkAllPresent} className="btn btn-secondary" style={{ border: '1px solid var(--color-success)', color: 'var(--color-success)' }}>
            💚 Mark All Present
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 600 }}>
            {saving && (
              <span style={{ color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span className="spinner" style={{ width: '12px', height: '12px' }}></span> Saving changes...
              </span>
            )}
            {saveSuccess && (
              <span style={{ color: 'var(--color-success)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                💚 Register synced with database.
              </span>
            )}
            {!saving && !saveSuccess && (
              <span style={{ color: 'var(--text-muted)' }}>💤 Sheet idle (auto-saves on changes)</span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: 'var(--color-danger-light)',
          color: 'var(--color-danger)',
          padding: '14px',
          borderRadius: 'var(--radius-sm)',
          fontWeight: 500,
          border: '1px solid hsl(355, 80%, 90%)'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Roster Listing Grid */}
      <div className="glass-panel" style={{ padding: '24px' }}>
        <div className="spreadsheet-container">
          <table className="spreadsheet-table">
            <thead>
              <tr>
                <th className="spreadsheet-th" style={{ width: '15%' }}>Admission No.</th>
                <th className="spreadsheet-th" style={{ width: '30%' }}>Student Name</th>
                <th className="spreadsheet-th" style={{ width: '10%' }}>Sex</th>
                <th className="spreadsheet-th" style={{ width: '25%' }}>Status Marks</th>
                <th className="spreadsheet-th" style={{ width: '20%' }}>Remarks / Reasons</th>
              </tr>
            </thead>
            <tbody>
              {records.map((row) => (
                <tr key={row.pupil_id} className="spreadsheet-tr">
                  <td className="spreadsheet-td" style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{row.admission_number}</td>
                  <td className="spreadsheet-td" style={{ fontWeight: 600 }}>{row.first_name} {row.last_name}</td>
                  <td className="spreadsheet-td">
                    <span style={{
                      backgroundColor: row.sex === 'M' ? 'hsl(220, 75%, 95%)' : 'hsl(320, 75%, 95%)',
                      color: row.sex === 'M' ? 'hsl(220, 75%, 45%)' : 'hsl(320, 75%, 45%)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 700
                    }}>
                      {row.sex}
                    </span>
                  </td>
                  <td className="spreadsheet-td">
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {(['P', 'A', 'L', 'E'] as const).map((status) => {
                        const isSelected = row.status === status;
                        const badgeInfo = statusBadges[status];
                        
                        return (
                          <button
                            key={status}
                            onClick={() => handleStatusChange(row.pupil_id, status)}
                            className="btn"
                            style={{
                              padding: '5px 10px',
                              fontSize: '12px',
                              borderRadius: '4px',
                              fontWeight: 700,
                              backgroundColor: isSelected ? badgeInfo.color : 'white',
                              color: isSelected ? 'white' : 'var(--text-muted)',
                              border: isSelected ? '1px solid transparent' : '1px solid var(--border-color)',
                              boxShadow: isSelected ? 'var(--shadow-sm)' : 'none'
                            }}
                          >
                            {status}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="spreadsheet-td">
                    <input
                      type="text"
                      value={row.remarks || ''}
                      onChange={(e) => handleRemarksChange(row.pupil_id, e.target.value)}
                      onBlur={() => triggerAutoSave(records)}
                      placeholder="Add reason/note..."
                      style={{
                        padding: '6px 10px',
                        fontSize: '13px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px'
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manual Sync Trigger */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button 
          onClick={() => triggerAutoSave(records)} 
          className="btn btn-primary" 
          disabled={saving}
          style={{ width: '220px' }}
        >
          {saving ? 'Saving changes...' : '💾 Verify & Sync Register'}
        </button>
      </div>

    </div>
  );
};

export default Attendance;
